import React, { useEffect, useState, useCallback } from 'react';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import EntrySheet from '../components/EntrySheet';
import { fmtDate } from '../utils/date';

const ASSET_CATEGORIES = ['equipment', 'vehicle', 'property', 'furniture', 'technology', 'livestock', 'other'];
const EMPTY_FORM = { name: '', category: 'equipment', value: '', supplier: '', acquired_at: '', description: '' };

const fmt = (n) =>
  new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

export default function CompanyAssets() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [businesses, setBusinesses] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [bizData, setBizData]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [editEntry, setEditEntry]   = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  useEffect(() => {
    companyAPI.listBiz().then(r => setBusinesses(r.data.businesses || []));
  }, []);

  const loadBiz = useCallback(async (biz) => {
    setSelectedBiz(biz);
    setShowForm(false);
    setForm(EMPTY_FORM);
    setError('');
    setLoading(true);
    try {
      const r = await companyAPI.getBusinessDetail(biz.id);
      setBizData(r.data);
    } catch {
      setBizData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const value = parseFloat(form.value);
    if (!form.name.trim()) { setError('Enter an asset name'); return; }
    if (!value || value <= 0) { setError('Enter a valid asset value'); return; }
    setSaving(true);
    try {
      await companyAPI.addBizAsset(selectedBiz.id, {
        name:        form.name.trim(),
        category:    form.category,
        value,
        supplier:    form.supplier    || undefined,
        acquired_at: form.acquired_at ? new Date(form.acquired_at).toISOString() : undefined,
        description: form.description || undefined,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadBiz(selectedBiz);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to record asset');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (a) => {
    setEditEntry(a);
    setEditForm({
      name:        a.name,
      category:    a.category,
      value:       String(parseFloat(Number(a.value).toFixed(2))),
      supplier:    a.supplier || '',
      acquired_at: a.acquired_at ? new Date(a.acquired_at).toISOString().slice(0, 16) : '',
      description: a.description || '',
    });
    setEditError('');
  };

  const handleEditSave = async () => {
    setEditError('');
    if (!editForm.name?.trim()) { setEditError('Enter an asset name'); return; }
    const value = parseFloat(editForm.value);
    if (!value || value <= 0) { setEditError('Enter a valid value'); return; }
    setEditSaving(true);
    try {
      await companyAPI.updateBizAsset(selectedBiz.id, editEntry.id, {
        name:        editForm.name.trim(),
        category:    editForm.category,
        value,
        supplier:    editForm.supplier || undefined,
        acquired_at: editForm.acquired_at ? new Date(editForm.acquired_at).toISOString() : undefined,
        description: editForm.description || undefined,
      });
      setEditEntry(null);
      await loadBiz(selectedBiz);
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditDelete = async () => {
    setEditSaving(true);
    try {
      await companyAPI.voidBizAsset(selectedBiz.id, editEntry.id);
      setEditEntry(null);
      await loadBiz(selectedBiz);
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to delete');
    } finally {
      setEditSaving(false);
    }
  };

  const assets   = bizData?.assets   || [];
  const currency = selectedBiz?.currency || '';
  const total    = assets.reduce((s, a) => s + Number(a.value), 0);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>
      <BackButton />
      <h1 style={{ margin: '0 0 1.2rem', fontSize: '1.4rem', fontWeight: 700 }}>Company Assets</h1>

      {/* Business selector */}
      <div style={{ marginBottom: '1.2rem' }}>
        <label style={{ fontSize: '0.82rem', color: 'var(--color-muted)', display: 'block', marginBottom: 4 }}>
          Select business
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {businesses.map(b => (
            <button
              key={b.id}
              onClick={() => loadBiz(b)}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: 20, border: '1px solid var(--color-border)',
                background: selectedBiz?.id === b.id ? 'var(--color-primary)' : 'transparent',
                color: selectedBiz?.id === b.id ? '#fff' : 'inherit',
                cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500,
              }}
            >
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {selectedBiz && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <span style={{ fontSize: '0.9rem', color: 'var(--color-muted)' }}>{selectedBiz.name} · {currency}</span>
              {assets.length > 0 && (
                <span style={{ marginLeft: '0.75rem', fontWeight: 700, color: 'var(--color-success)' }}>
                  {currency} {fmt(total)}
                </span>
              )}
            </div>
            <button
              onClick={() => { setShowForm(s => !s); setError(''); }}
              style={{
                background: 'var(--color-primary)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '0.45rem 1rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              {showForm ? 'Cancel' : '+ Add Asset'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} style={{
              background: 'var(--color-card)', borderRadius: 12, padding: '1.2rem',
              marginBottom: '1.2rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
            }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Asset name</span>
                <input
                  type="text" required value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Honey extractor, Delivery van"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Category</span>
                <select
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                >
                  {ASSET_CATEGORIES.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Value ({currency})</span>
                <input
                  type="number" min="0.01" step="any" required value={form.value}
                  onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                  placeholder="0.00"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Supplier / source <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="text" value={form.supplier}
                  onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                  placeholder="e.g. Farm Supplies Ltd"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Acquired date <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="datetime-local" value={form.acquired_at}
                  onChange={e => setForm(f => ({ ...f, acquired_at: e.target.value }))}
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>Description <span style={{ fontStyle: 'italic' }}>(optional)</span></span>
                <input
                  type="text" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. 50-frame radial extractor"
                  style={{ padding: '0.55rem', borderRadius: 8, border: '1px solid var(--color-border)' }}
                />
              </label>

              {error && <div style={{ color: 'var(--color-danger)', fontSize: '0.88rem' }}>{error}</div>}

              <button
                type="submit" disabled={saving}
                style={{
                  background: 'var(--color-primary)', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '0.65rem', fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Record Asset'}
              </button>
            </form>
          )}

          {loading ? (
            <div style={{ color: 'var(--color-muted)', textAlign: 'center', marginTop: '2rem' }}>Loading…</div>
          ) : assets.length === 0 ? (
            <div style={{ color: 'var(--color-muted)', textAlign: 'center', marginTop: '2rem' }}>
              No assets recorded for {selectedBiz.name}.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {assets.map(a => (
                <div key={a.id} onClick={isOwner ? () => openEdit(a) : undefined} style={{
                  background: 'var(--color-card)', borderRadius: 10, padding: '0.85rem 1rem',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: isOwner ? 'pointer' : 'default',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{a.name}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>
                      {a.category.charAt(0).toUpperCase() + a.category.slice(1)}
                      {a.supplier ? ` · ${a.supplier}` : ''}
                      {a.description ? ` · ${a.description}` : ''}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>{fmtDate(a.acquired_at)}</div>
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--color-success)', whiteSpace: 'nowrap' }}>
                    {currency} {fmt(a.value)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isOwner && <EntrySheet
        open={!!editEntry}
        onClose={() => setEditEntry(null)}
        title={editEntry ? `Edit: ${editEntry.name}` : ''}
        fields={[
          { key: 'name',        label: 'Asset name', required: true },
          { key: 'category',    label: 'Category', options: ['equipment','vehicle','property','furniture','technology','livestock','other'].map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) })) },
          { key: 'value',       label: `Value (${currency})`, type: 'number', required: true },
          { key: 'supplier',    label: 'Supplier / source' },
          { key: 'acquired_at', label: 'Acquired date', type: 'datetime-local' },
          { key: 'description', label: 'Description' },
        ]}
        values={editForm}
        onChange={(k, v) => setEditForm(f => ({ ...f, [k]: v }))}
        onSave={handleEditSave}
        onDelete={handleEditDelete}
        saving={editSaving}
        error={editError}
      />}
    </div>
  );
}
